import type { Core, EventObject } from 'cytoscape'
import { useCallback, useEffect, useState } from 'react'

export interface CanvasMenuPosition {
  /** Where to draw the menu: container pixels. */
  rendered: { x: number; y: number }
  /** Where a node created from the menu goes: graph coordinates. */
  model: { x: number; y: number }
}

/**
 * A right-click on the empty canvas (not on a node or an edge, which have the
 * ring menu) opens a context menu at the pointer. Any tap, pan or zoom closes it.
 */
export function useCanvasContextMenu(cy: Core | null, enabled: boolean) {
  const [menu, setMenu] = useState<CanvasMenuPosition | null>(null)
  const close = useCallback(() => setMenu(null), [])
  const isOpen = menu !== null

  useEffect(() => {
    if (!cy || !enabled) return
    const open = (evt: EventObject) => {
      if (evt.target !== cy) return
      setMenu({
        rendered: { x: evt.renderedPosition.x, y: evt.renderedPosition.y },
        model: { x: evt.position.x, y: evt.position.y },
      })
    }
    cy.on('cxttap', open)
    return () => {
      cy.off('cxttap', open)
    }
  }, [cy, enabled])

  // `viewport` fires on every pan and zoom frame: listen only while a menu is open.
  useEffect(() => {
    if (!cy || !isOpen) return
    cy.on('tap viewport', close)
    return () => {
      cy.off('tap viewport', close)
    }
  }, [cy, isOpen, close])

  useEffect(() => {
    if (!enabled) setMenu(null)
  }, [enabled])

  return { menu, close }
}
